import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { caseService, inputService, judicialService, api } from "@/services/api";
import { mapDbCaseToFrontend, mapDbInterventionToFrontend, mapDbJudicialToFrontend, mapDbInsumoToFrontend } from "@/services/mapping";

function daysBetween(d1: string, d2: string): number {
  return Math.ceil((new Date(d2).getTime() - new Date(d1).getTime()) / (1000 * 60 * 60 * 24));
}

function isOverdue(dateStr: string, todayStr: string): boolean {
  return new Date(dateStr) < new Date(todayStr);
}

export default function AlertasPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cases, setCases] = useState<any[]>([]);
  const [interventions, setInterventions] = useState<any[]>([]);
  const [courtRequests, setCourtRequests] = useState<any[]>([]);
  const [teacherInputs, setTeacherInputs] = useState<any[]>([]);

  const todayStr = new Date().toISOString().split("T")[0];

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [casesData, inputsData, judicialData, interventionsData] = await Promise.all([
        caseService.list(),
        inputService.listPendientes(),
        judicialService.list(),
        api.get("/intervenciones").then(res => res.data)
      ]);

      if (Array.isArray(casesData)) {
        setCases(casesData.map(mapDbCaseToFrontend));
      }
      if (Array.isArray(inputsData)) {
        setTeacherInputs(inputsData.map(mapDbInsumoToFrontend));
      }
      if (Array.isArray(judicialData)) {
        setCourtRequests(judicialData.map(mapDbJudicialToFrontend));
      }
      if (Array.isArray(interventionsData)) {
        setInterventions(interventionsData.map(mapDbInterventionToFrontend));
      }
    } catch (err: any) {
      console.error("Failed to load alerts from Azure database:", err);
      setError("Error al cargar alertas en tiempo real de Azure: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const casoInterventions = useMemo(() => {
    const map: Record<string, any[]> = {};
    interventions.forEach((i) => {
      if (!map[i.caseId]) map[i.caseId] = [];
      map[i.caseId].push(i);
    });
    return map;
  }, [interventions]);

  const casosActivos = useMemo(() => {
    return cases.filter((c) => c.estado !== "cerrado");
  }, [cases]);

  const casosCriticos = useMemo(() => {
    return casosActivos.filter((c) => {
      const ints = casoInterventions[c.id] || [];
      const lastInt = ints.length > 0
        ? ints.reduce((latest, curr) => curr.fecha > latest.fecha ? curr : latest, ints[0])
        : null;
      const daysSince = lastInt ? daysBetween(lastInt.fecha, todayStr) : daysBetween(c.fechaApertura, todayStr);
      const overdueInput = teacherInputs.some((t) => t.caseId === c.id && (t.estado === "vencido" || (t.estado === "pendiente" && isOverdue(t.fechaLimite, todayStr))));
      return c.prioridad === "critica" || c.prioridad === "critico" || overdueInput || daysSince > 10;
    });
  }, [casosActivos, casoInterventions, teacherInputs, todayStr]);

  const informesVencidos = useMemo(() => {
    return courtRequests.filter((cr) => cr.estado !== "respondido" && cr.estado !== "enviado" && isOverdue(cr.fechaLimite, todayStr));
  }, [courtRequests, todayStr]);

  const informesProximos = useMemo(() => {
    return courtRequests.filter((cr) => {
      const d = daysBetween(todayStr, cr.fechaLimite);
      return cr.estado !== "respondido" && cr.estado !== "enviado" && d >= 0 && d <= 5;
    });
  }, [courtRequests, todayStr]);

  const insumosVencidos = useMemo(() => {
    return teacherInputs.filter((t) => t.estado === "vencido" || (t.estado === "pendiente" && isOverdue(t.fechaLimite, todayStr)));
  }, [teacherInputs, todayStr]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line text-2xl text-foreground-300 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-800 text-xs rounded-xl font-medium">
          {error}
        </div>
      )}

      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Alertas Críticas</h1>
        <p className="text-sm text-foreground-500 mt-0.5">Casos, informes y solicitudes que requieren atención inmediata</p>
      </div>

      {/* Casos críticos */}
      <div className="bg-white border border-background-200/80 rounded-2xl p-5">
        <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-4 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
          Casos en estado crítico o inactivos por más de 10 días
          <span className="text-xs font-normal text-foreground-400 normal-case">({casosCriticos.length})</span>
        </h2>
        {casosCriticos.length === 0 ? (
          <div className="text-center py-8 text-foreground-400">
            <i className="ri-check-line text-2xl mb-1 block" />
            <p className="text-xs">No hay casos críticos</p>
          </div>
        ) : (
          <div className="space-y-2">
            {casosCriticos.map((c) => (
              <div key={c.id} onClick={() => navigate(`/casos/${c.id}`)} className="flex items-center justify-between p-3 bg-red-50/50 border border-red-100 rounded-xl hover:bg-red-100/60 transition-colors cursor-pointer text-left">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-900">{c.studentName} · {c.curso}</p>
                  <p className="text-xs text-foreground-500 truncate">{c.titulo}</p>
                </div>
                <span className="text-xs font-bold text-red-600 bg-white px-2.5 py-1 rounded-full whitespace-nowrap ml-3 border border-red-100">
                  {c.prioridad === "critica" || c.prioridad === "critico" ? "CRÍTICO" : "REVISAR"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Informes vencidos */}
      <div className="bg-white border border-background-200/80 rounded-2xl p-5">
        <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-4 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
          Informes de tribunal vencidos o próximos
          <span className="text-xs font-normal text-foreground-400 normal-case">({informesVencidos.length + informesProximos.length})</span>
        </h2>
        
        {informesVencidos.length === 0 && informesProximos.length === 0 ? (
          <div className="text-center py-8 text-foreground-400">
            <i className="ri-check-line text-2xl mb-1 block" />
            <p className="text-xs">No hay informes de tribunal pendientes</p>
          </div>
        ) : (
          <div className="space-y-2">
            {informesVencidos.map((cr) => {
              const caso = cases.find((c) => c.id === cr.caseId);
              return (
                <div key={cr.id} onClick={() => navigate(cr.caseId ? `/casos/${cr.caseId}` : "/orientacion/informes")} className="flex items-center justify-between p-3 bg-red-50/50 border border-red-100 rounded-xl hover:bg-red-100/60 transition-colors cursor-pointer text-left">
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{caso?.studentName || cr.studentName || "—"}</p>
                    <p className="text-xs text-foreground-500">{cr.tribunal} · {cr.rit || cr.ruc || "—"}</p>
                  </div>
                  <span className="text-xs font-bold text-red-600 bg-white px-2.5 py-1 rounded-full whitespace-nowrap border border-red-100">VENCIDO {cr.fechaLimite}</span>
                </div>
              );
            })}
            {informesProximos.map((cr) => {
              const caso = cases.find((c) => c.id === cr.caseId);
              const d = daysBetween(todayStr, cr.fechaLimite);
              return (
                <div key={cr.id} onClick={() => navigate(cr.caseId ? `/casos/${cr.caseId}` : "/orientacion/informes")} className="flex items-center justify-between p-3 bg-amber-50/50 border border-amber-100 rounded-xl hover:bg-amber-100/60 transition-colors cursor-pointer text-left">
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{caso?.studentName || cr.studentName || "—"}</p>
                    <p className="text-xs text-foreground-500">{cr.tribunal} · {cr.rit || cr.ruc || "—"}</p>
                  </div>
                  <span className="text-xs font-bold text-amber-600 bg-white px-2.5 py-1 rounded-full whitespace-nowrap border border-amber-100">{d}d restantes</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Insumos vencidos */}
      <div className="bg-white border border-background-200/80 rounded-2xl p-5">
        <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-4 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
          Insumos docentes vencidos
          <span className="text-xs font-normal text-foreground-400 normal-case">({insumosVencidos.length})</span>
        </h2>
        {insumosVencidos.length === 0 ? (
          <div className="text-center py-8 text-foreground-400">
            <i className="ri-check-line text-2xl mb-1 block" />
            <p className="text-xs">No hay insumos docentes vencidos</p>
          </div>
        ) : (
          <div className="space-y-2">
            {insumosVencidos.map((ti) => {
              const caso = cases.find((c) => c.id === ti.caseId);
              return (
                <div key={ti.id} onClick={() => navigate(ti.caseId ? `/casos/${ti.caseId}` : "/orientacion/insumos")} className="flex items-center justify-between p-3 bg-red-50/50 border border-red-100 rounded-xl hover:bg-red-100/60 transition-colors cursor-pointer text-left">
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{ti.docenteName}</p>
                    <p className="text-xs text-foreground-500">{ti.tipoInsumo} · Estudiante: {caso?.studentName || ti.studentName || "—"}</p>
                  </div>
                  <span className="text-xs font-bold text-red-600 bg-white px-2.5 py-1 rounded-full whitespace-nowrap border border-red-100">VENCIDO {ti.fechaLimite}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}